import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ScatterChart, Scatter } from 'recharts';
import { useProductionData } from '../../contexts/ProductionDataContext';

const SamplingData = ({ objectName, objectType }) => {
  const { 
    getDevelopmentSampling, 
    getStopingSampling, 
    getTrammingSampling,
    getHoistingSampling,
    getTruckingSampling,
    loading: dataLoading 
  } = useProductionData();

  const [, setGeologyData] = useState(null);
  const [stopingSamplingData, setStopingSamplingData] = useState([]);
  const [developmentSamplingData, setDevelopmentSamplingData] = useState([]);
  const [trammingSamplingData, setTrammingSamplingData] = useState([]);
  const [, setHoistingSamplingData] = useState([]);
  const [, setTruckingSamplingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setStopingSamplingLoading] = useState(false);
  const [, setDevelopmentSamplingLoading] = useState(false);
  const [, setTrammingSamplingLoading] = useState(false);
  const [, setHoistingSamplingLoading] = useState(false);
  const [, setTruckingSamplingLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [xAxis, setXAxis] = useState('sampling_date');
  const [yAxis, setYAxis] = useState('grade_pk_br');
  const [chartType, setChartType] = useState('bar');

  // Pagination — use the active sampling set for the current object type
  const activeSamplingData = objectType === 'development'
    ? developmentSamplingData
    : objectType === 'tramming'
      ? trammingSamplingData
      : stopingSamplingData;

  const totalPages = Math.max(1, Math.ceil(activeSamplingData.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = activeSamplingData.slice(startIndex, endIndex);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSamplingData]);

  // Format date for better readability
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };

  // Filter sampling data by objectName
  const filterSamplingByStope = (data, isDevelopment = false) => {
    if (!objectName || !data.length) return data;
    
    return data.filter(item => {
      // For development sampling, use dev_end_id
      // For other sampling, use stope_id
      const databaseID = isDevelopment 
        ? (item.dev_end_id || item.stope_id)
        : (item.stope_id);
      
      if (!databaseID) return false;
      
      const parameterObjectName = objectName;
      
      // Check if database ID is at least 5 characters long and contains "L_"
      if (databaseID.length < 5 || !databaseID.includes('L_')) {
        return false;
      }
      
      if (parameterObjectName.startsWith(databaseID)) {
        const nextChar = parameterObjectName.charAt(databaseID.length);
        if (!nextChar || isNaN(parseInt(nextChar))) {
          return true;
        }
      }
      
      return false;
    });
  };

  // Get raw data from ProductionDataHandler
  const rawDevelopmentSampling = getDevelopmentSampling();
  const rawStopingSampling = getStopingSampling();
  const rawTrammingSampling = getTrammingSampling();
  const rawHoistingSampling = getHoistingSampling();
  const rawTruckingSampling = getTruckingSampling();

  // Use refs to track previous data lengths to prevent infinite loops
  const prevObjectNameRef = useRef(objectName);
  const prevDevLengthRef = useRef(0);
  const prevStopingLengthRef = useRef(0);
  const prevTrammingLengthRef = useRef(0);
  const prevHoistingLengthRef = useRef(0);
  const prevTruckingLengthRef = useRef(0);

  // Process and filter data when objectName or raw data changes
  useEffect(() => {
    // Check if objectName changed or data lengths changed
    const objectNameChanged = prevObjectNameRef.current !== objectName;
    const devChanged = prevDevLengthRef.current !== rawDevelopmentSampling.length;
    const stopingChanged = prevStopingLengthRef.current !== rawStopingSampling.length;
    const trammingChanged = prevTrammingLengthRef.current !== rawTrammingSampling.length;
    const hoistingChanged = prevHoistingLengthRef.current !== rawHoistingSampling.length;
    const truckingChanged = prevTruckingLengthRef.current !== rawTruckingSampling.length;

    if (!objectNameChanged && !devChanged && !stopingChanged && !trammingChanged && !hoistingChanged && !truckingChanged) {
      // Nothing changed, skip processing
      return;
    }

    // Update refs
    prevObjectNameRef.current = objectName;
    prevDevLengthRef.current = rawDevelopmentSampling.length;
    prevStopingLengthRef.current = rawStopingSampling.length;
    prevTrammingLengthRef.current = rawTrammingSampling.length;
    prevHoistingLengthRef.current = rawHoistingSampling.length;
    prevTruckingLengthRef.current = rawTruckingSampling.length;

    if (!objectName) {
      setStopingSamplingData([]);
      setDevelopmentSamplingData([]);
      setTrammingSamplingData([]);
      setHoistingSamplingData([]);
      setTruckingSamplingData([]);
      return;
    }

    setLoading(true);
    setStopingSamplingLoading(true);
    setDevelopmentSamplingLoading(true);
    setTrammingSamplingLoading(true);
    setHoistingSamplingLoading(true);
    setTruckingSamplingLoading(true);

    try {
      // Process stoping sampling data (formerly bench sampling)
      const filteredStopingData = filterSamplingByStope(rawStopingSampling, false);
      console.log('[SamplingData] Stoping sampling - Original data count:', rawStopingSampling.length);
      console.log('[SamplingData] Stoping sampling - Filtered data count:', filteredStopingData.length);
      setStopingSamplingData(filteredStopingData);
      setStopingSamplingLoading(false);

      // Generate summary data from stoping sampling
      if (filteredStopingData.length > 0) {
        const grades = filteredStopingData
          .map(item => parseFloat(item.grade_pk_br || item.grade_pk_fa || 0))
          .filter(grade => !isNaN(grade) && grade > 0);
        const dates = filteredStopingData
          .map(item => new Date(item.sampling_date))
          .filter(date => !isNaN(date.getTime()));
        
        const summaryData = {
          sampleCount: filteredStopingData.length,
          averageGrade: grades.length > 0 ? (grades.reduce((sum, grade) => sum + grade, 0) / grades.length).toFixed(2) : 'N/A',
          minDate: dates.length > 0 ? formatDate(new Date(Math.min(...dates))) : 'N/A',
          maxDate: dates.length > 0 ? formatDate(new Date(Math.max(...dates))) : 'N/A',
          gradeRange: grades.length > 0 ? `${Math.min(...grades).toFixed(2)} - ${Math.max(...grades).toFixed(2)}` : 'N/A'
        };
        
        setGeologyData(summaryData);
      } else {
        setGeologyData({
          sampleCount: 0,
          averageGrade: 'N/A',
          minDate: 'N/A',
          maxDate: 'N/A',
          gradeRange: 'N/A'
        });
      }

      // Process development sampling data
      const filteredDevelopmentData = filterSamplingByStope(rawDevelopmentSampling, true);
      console.log('[SamplingData] Development sampling - Original data count:', rawDevelopmentSampling.length);
      console.log('[SamplingData] Development sampling - Filtered data count:', filteredDevelopmentData.length);
      setDevelopmentSamplingData(filteredDevelopmentData);
      setDevelopmentSamplingLoading(false);

      // Process tramming sampling data
      const filteredTrammingData = filterSamplingByStope(rawTrammingSampling, false);
      console.log('[SamplingData] Tramming sampling - Original data count:', rawTrammingSampling.length);
      console.log('[SamplingData] Tramming sampling - Filtered data count:', filteredTrammingData.length);
      setTrammingSamplingData(filteredTrammingData);
      setTrammingSamplingLoading(false);

      // Process hoisting sampling data
      const filteredHoistingData = filterSamplingByStope(rawHoistingSampling, false);
      setHoistingSamplingData(filteredHoistingData);
      setHoistingSamplingLoading(false);

      // Process trucking sampling data
      const filteredTruckingData = filterSamplingByStope(rawTruckingSampling, false);
      setTruckingSamplingData(filteredTruckingData);
      setTruckingSamplingLoading(false);

      setLoading(false);
    } catch (err) {
      console.error('[SamplingData] Error processing data:', err);
      setStopingSamplingData([]);
      setDevelopmentSamplingData([]);
      setTrammingSamplingData([]);
      setHoistingSamplingData([]);
      setTruckingSamplingData([]);
      setLoading(false);
      setStopingSamplingLoading(false);
      setDevelopmentSamplingLoading(false);
      setTrammingSamplingLoading(false);
      setHoistingSamplingLoading(false);
      setTruckingSamplingLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectName, objectType, rawDevelopmentSampling.length, rawStopingSampling.length, rawTrammingSampling.length, rawHoistingSampling.length, rawTruckingSampling.length]);

  if (dataLoading || loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-2 text-zinc-400">
          {dataLoading ? 'Loading sampling data...' : 'Processing sampling data...'}
        </span>
      </div>
    );
  }

  // Get numeric columns for Y axis
  const getNumericColumns = (data) => {
    if (!data || data.length === 0) return [];
    const numericFields = ['grade_pk_br', 'grade_pk_fa', 'grade_jv_br'];
    return numericFields.filter(field => data.some(item => !isNaN(parseFloat(item[field])) && item[field] > 0));
  };

  // Process data for chart
  const processChartData = (data, xAxisField, yAxisField) => {
    if (!data || data.length === 0) return [];

    const grouped = {};
    const originalDates = {};
    
    data.forEach(item => {
      let key = item[xAxisField];
      let originalDate = null;
      
      if (xAxisField === 'sampling_date') {
        originalDate = item[xAxisField];
        key = formatDate(key);
      } else if (xAxisField === 'Month') {
        const date = new Date(item.sampling_date);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          key = `${month}_${year}`;
        } else {
          key = 'Invalid Date';
        }
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [xAxisField]: key,
          count: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      // Add numeric fields
      const numericFields = getNumericColumns(data);
      numericFields.forEach(field => {
        const value = parseFloat(item[field]);
        if (!isNaN(value) && value > 0) {
          if (!grouped[key][`total_${field}`]) grouped[key][`total_${field}`] = 0;
          grouped[key][`total_${field}`] += value;
        }
      });
    });

    // Calculate averages and return processed data
    const processedData = Object.values(grouped).map(group => {
      const processed = { [xAxisField]: group[xAxisField], count: group.count };
      
      const numericFields = getNumericColumns(data);
      numericFields.forEach(field => {
        const total = group[`total_${field}`];
        if (total !== undefined) {
          const average = total / group.count;
          processed[field] = parseFloat(average.toFixed(2));
          processed[`${field}_total`] = total;
        }
      });
      
      return processed;
    });

    // Sort by date if X-axis is sampling_date
    if (xAxisField === 'sampling_date') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[xAxisField]]);
        const dateB = new Date(originalDates[b[xAxisField]]);
        return dateA - dateB;
      });
    }

    if (xAxisField === 'Month') {
      return processedData.sort((a, b) => {
        const [monthA, yearA] = a[xAxisField].split('_');
        const [monthB, yearB] = b[xAxisField].split('_');
        const yearCompare = parseInt(yearA) - parseInt(yearB);
        if (yearCompare !== 0) return yearCompare;
        return parseInt(monthA) - parseInt(monthB);
      });
    }

    return processedData;
  };

  const chartData = processChartData(activeSamplingData, xAxis, yAxis);

  // Calculate summary statistics
  const calculateSummaryStats = (data, gradeField = 'grade_pk_br') => {
    if (!data || data.length === 0) {
      return {
        sampleCount: 0,
        averageGrade: 'N/A',
        minDate: 'N/A',
        maxDate: 'N/A',
        gradeRange: 'N/A'
      };
    }

    const grades = data
      .map(item => parseFloat(item[gradeField] || item.grade_pk_fa || 0))
      .filter(grade => !isNaN(grade) && grade > 0);
    const dates = data
      .map(item => new Date(item.sampling_date))
      .filter(date => !isNaN(date.getTime()));
    
    return {
      sampleCount: data.length,
      averageGrade: grades.length > 0 ? (grades.reduce((sum, grade) => sum + grade, 0) / grades.length).toFixed(2) : 'N/A',
      minDate: dates.length > 0 ? formatDate(new Date(Math.min(...dates))) : 'N/A',
      maxDate: dates.length > 0 ? formatDate(new Date(Math.max(...dates))) : 'N/A',
      gradeRange: grades.length > 0 ? `${Math.min(...grades).toFixed(2)} - ${Math.max(...grades).toFixed(2)}` : 'N/A'
    };
  };

  const typeStats = calculateSummaryStats(activeSamplingData, 'grade_pk_br');
  const typeStatsFA = calculateSummaryStats(activeSamplingData, 'grade_pk_fa');
  const typeLabel = objectType === 'development'
    ? 'Development'
    : objectType === 'tramming'
      ? 'Tramming'
      : objectType === 'hoisting'
        ? 'Hoisting'
        : 'Stoping';

  return (
    <div className="space-y-2">
      {/* Type-specific summary */}
      <div className="overflow-x-auto border border-yellow-600/25 rounded">
        <table className="min-w-full text-xs">
          <thead className="bg-zinc-900/80">
            <tr>
              <th className="px-1.5 py-1 text-left font-medium text-zinc-500 border-r border-yellow-600/25">Metric</th>
              <th className="px-1.5 py-1 text-center font-medium text-zinc-500">{typeLabel}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-zinc-800">
              <td className="px-1.5 py-0.5 font-medium text-zinc-300 border-r border-zinc-800">Samples</td>
              <td className="px-1.5 py-0.5 text-center text-zinc-100">{typeStats.sampleCount}</td>
            </tr>
            <tr className="border-t border-zinc-800 bg-zinc-900/80">
              <td className="px-1.5 py-0.5 font-medium text-zinc-300 border-r border-zinc-800">Avg Grade PK BR</td>
              <td className="px-1.5 py-0.5 text-center text-zinc-100">{typeStats.averageGrade}</td>
            </tr>
            <tr className="border-t border-zinc-800">
              <td className="px-1.5 py-0.5 font-medium text-zinc-300 border-r border-zinc-800">Avg Grade PK FA</td>
              <td className="px-1.5 py-0.5 text-center text-zinc-100">{typeStatsFA.averageGrade}</td>
            </tr>
            <tr className="border-t border-zinc-800 bg-zinc-900/80">
              <td className="px-1.5 py-0.5 font-medium text-zinc-300 border-r border-zinc-800">Date Range</td>
              <td className="px-1.5 py-0.5 text-center text-zinc-100">{typeStats.minDate} – {typeStats.maxDate}</td>
            </tr>
            <tr className="border-t border-zinc-800">
              <td className="px-1.5 py-0.5 font-medium text-zinc-300 border-r border-zinc-800">Grade Range</td>
              <td className="px-1.5 py-0.5 text-center text-zinc-100">{typeStats.gradeRange}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {activeSamplingData.length === 0 ? (
        <p className="text-xs text-zinc-500 py-2">No {typeLabel.toLowerCase()} sampling records for this object.</p>
      ) : (
        <>
          <div className="border border-yellow-600/25 rounded overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-zinc-900/80">
                <tr>
                  <th className="px-1.5 py-1 text-left font-medium text-zinc-500 border-r border-zinc-800">Date</th>
                  <th className="px-1.5 py-1 text-left font-medium text-zinc-500 border-r border-zinc-800">Sample ID</th>
                  <th className="px-1.5 py-1 text-left font-medium text-zinc-500 border-r border-zinc-800">PK BR</th>
                  <th className="px-1.5 py-1 text-left font-medium text-zinc-500 border-r border-zinc-800">PK FA</th>
                  <th className="px-1.5 py-1 text-left font-medium text-zinc-500">JV BR</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-zinc-950' : 'bg-zinc-900/80'}>
                    <td className="px-1.5 py-0.5 whitespace-nowrap text-zinc-200 border-r border-zinc-800">{formatDate(item.sampling_date)}</td>
                    <td className="px-1.5 py-0.5 whitespace-nowrap text-zinc-200 border-r border-zinc-800">{item.samp_id || 'N/A'}</td>
                    <td className="px-1.5 py-0.5 whitespace-nowrap text-zinc-200 border-r border-zinc-800">
                      {item.grade_pk_br ? parseFloat(item.grade_pk_br).toFixed(2) : '—'}
                    </td>
                    <td className="px-1.5 py-0.5 whitespace-nowrap text-zinc-200 border-r border-zinc-800">
                      {item.grade_pk_fa ? parseFloat(item.grade_pk_fa).toFixed(2) : '—'}
                    </td>
                    <td className="px-1.5 py-0.5 whitespace-nowrap text-zinc-200">
                      {item.grade_jv_br ? parseFloat(item.grade_jv_br).toFixed(2) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {activeSamplingData.length > itemsPerPage && (
              <div className="flex items-center justify-center gap-1 py-1 border-t border-zinc-800 bg-zinc-900/80">
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="m-0 px-1.5 py-0.5 text-[10px] border border-yellow-600/35 rounded-sm text-yellow-500 hover:bg-yellow-500/10 disabled:opacity-40"
                >
                  ‹
                </button>
                <span className="text-[10px] text-zinc-400">{currentPage}/{totalPages}</span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="m-0 px-1.5 py-0.5 text-[10px] border border-yellow-600/35 rounded-sm text-yellow-500 hover:bg-yellow-500/10 disabled:opacity-40"
                >
                  ›
                </button>
              </div>
            )}
          </div>

          {chartData.length > 0 && (
            <div className="border border-yellow-600/25 rounded p-2">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <select
                  value={xAxis}
                  onChange={(e) => setXAxis(e.target.value)}
                  className="text-xs border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 px-1.5 py-0.5"
                >
                  <option value="sampling_date">Date</option>
                  <option value="Month">Month</option>
                </select>
                <select
                  value={yAxis}
                  onChange={(e) => setYAxis(e.target.value)}
                  className="text-xs border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 px-1.5 py-0.5"
                >
                  <option value="count">Count</option>
                  {getNumericColumns(activeSamplingData).map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  className="text-xs border border-yellow-600/35 rounded-sm bg-zinc-950 text-zinc-200 px-1.5 py-0.5"
                >
                  <option value="bar">Bar</option>
                  <option value="line">Line</option>
                  <option value="scatter">Scatter</option>
                </select>
              </div>

              <div style={{ height: '220px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === 'bar' && (
                    <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 32 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
                      <XAxis dataKey={xAxis} angle={-45} textAnchor="end" height={48} fontSize={10} tick={{ fill: '#a1a1aa' }} stroke="#52525b" />
                      <YAxis fontSize={10} tick={{ fill: '#a1a1aa' }} stroke="#52525b" />
                      <Tooltip contentStyle={{ background:"#09090b", border:"1px solid rgba(234,179,8,0.35)", borderRadius:2, fontSize:11, color:"#e4e4e7" }} />
                      <Bar dataKey={yAxis} fill="#eab308" />
                    </BarChart>
                  )}
                  {chartType === 'line' && (
                    <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 32 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
                      <XAxis dataKey={xAxis} angle={-45} textAnchor="end" height={48} fontSize={10} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                      <YAxis fontSize={10} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                      <Tooltip contentStyle={{ background:"#09090b", border:"1px solid rgba(234,179,8,0.35)", borderRadius:2, fontSize:11, color:"#e4e4e7" }} />
                      <Line type="monotone" dataKey={yAxis} stroke="#eab308" strokeWidth={2} />
                    </LineChart>
                  )}
                  {chartType === 'scatter' && (
                    <ScatterChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 32 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
                      <XAxis dataKey={xAxis} angle={-45} textAnchor="end" height={48} fontSize={10} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                      <YAxis fontSize={10} tick={{ fill: "#a1a1aa" }} stroke="#52525b" />
                      <Tooltip contentStyle={{ background:"#09090b", border:"1px solid rgba(234,179,8,0.35)", borderRadius:2, fontSize:11, color:"#e4e4e7" }} />
                      <Scatter dataKey={yAxis} fill="#eab308" />
                    </ScatterChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SamplingData;
